import { Button } from "components/core/button"
import { Card } from "components/core/card"
import { Container } from "components/core/container"
import { Link } from "components/core/link"
import { PageTitle } from "components/core/pageTitle"
import { Spinner } from "components/core/spinner"
import { useSubscription } from "components/user/useSubscription"
import React, { FC } from "react"
import { Stripe } from "stripe"
import { useCustomerPortal } from "./useCustomerPortal"

interface Props {
  products: Stripe.Product[]
}

export const Account: FC<Props> = ({ products }) => {
  const { mutateAsync: createPortalLink, isLoading } = useCustomerPortal()
  const { data: subscription, isLoading: isLoadingSubscription } = useSubscription()
  const subscriptionItem = subscription?.items.data[0]
  const subscriptionPrice =
    subscriptionItem?.price.unit_amount &&
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: subscriptionItem.price.currency,
      minimumFractionDigits: 0,
    }).format(subscriptionItem.price.unit_amount / 100)

  const redirectToCustomerPortal = async () => {
    const url = await createPortalLink()
    window.location.assign(url)
  }

  const subscriptionProduct = products.find((p) => p.id === subscriptionItem?.price.product)
  const subscriptionName = subscriptionProduct ? subscriptionProduct.name : "Hobby"

  return (
    <Container>
      <PageTitle title="Account" subtitle="Find the basic information of your account." />

      <Card className="p-8 rounded-md lg:rounded-xl">
        <h3 className="text-2xl font-semibold mb-5 tracking-tight">Your plan</h3>

        {!isLoadingSubscription ? (
          <>
            {subscription ? (
              <p className="text-gray-500 dark:text-gray-400">
                {subscription.cancel_at ? (
                  <>
                    Your are still on the <strong>{subscriptionName} plan</strong> for{" "}
                    <strong>
                      {subscriptionPrice}/{subscriptionItem?.price.recurring?.interval}
                    </strong>{" "}
                    but you canceled it.
                    <br />
                    The subscription will
                    <strong>expire on {new Date(subscription.cancel_at).toLocaleDateString()}</strong>
                  </>
                ) : (
                  <>
                    You are currently on the <strong>{subscriptionName} plan</strong> for{" "}
                    <strong>
                      {subscriptionPrice}/{subscriptionItem?.price.recurring?.interval}
                    </strong>
                    .
                  </>
                )}
              </p>
            ) : (
              <p className="text-gray-500 dark:text-gray-400">
                You are currently on the <strong>{subscriptionName} plan</strong>.
              </p>
            )}
            <div className="mt-5">
              {subscription ? (
                <Button
                  variant="primary"
                  loading={isLoading}
                  disabled={isLoading || !subscription}
                  onClick={redirectToCustomerPortal}
                >
                  {subscription.cancel_at ? "Renew plan" : "Change plan"}
                </Button>
              ) : (
                <Link variant="primary" to="/upgrade">
                  Upgrade now
                </Link>
              )}
            </div>
          </>
        ) : (
          <Spinner className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
        )}
      </Card>
    </Container>
  )
}
